import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import {
    map,
    startWith,
    take,
    takeWhile
} from 'rxjs/operators';
import * as fromLookup from '../../../lookups';
import {
    IDirectReport,
    IExpenseItem,
    ILocale,
    IPerDiemLocale,
    ITask
} from '../../../lookups';
import { Process } from '../../../shared';
import {
    DynamicDialogConfig,
    DynamicDialogRef
} from '../../../shared/efaact-primeng.module';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { IUserInfo } from '../../../user-info';
import * as fromSettings from '../../../company-setting';
import { ExpenseRequestTypeEnum, TravelTypeEnum } from '../../expense-enums';
import { ExpenseService } from '../../expense.service';
import { IExpenseLine } from '../../IExpenseLine';
import { IExpenseSummary } from '../../IExpenseSummary';
import { IExpenseTask } from '../../IExpenseTask';
import { ITravelMode } from '../../ITravelMode';
import { ITravelRate } from '../../ITravelRate';
import { ITripLegType } from '../../ITripLegType';
import { UserPaymentMethod } from '../../UserPaymentMethod';
import * as fromExpense from '../../store/index';
import {
    ExpenseEntrySavePayload,
    ExpenseEntryTravelRatesRequest
} from '../../presenters/expense-entry/expense-entry.component';

export interface ExpenseEntryDialogData {
    process: Process;
    superUser: boolean;
    userInfo: IUserInfo;
    directReport: IDirectReport;
    canEdit: boolean;
    travelTypeId: TravelTypeEnum;
    summary?: IExpenseSummary;
    detail?: IExpenseLine;
}

@Component({
    selector: 'ef-expense-entry-feature',
    templateUrl: './expense-entry-feature.component.html',
    styleUrls: ['./expense-entry-feature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpenseEntryFeatureComponent implements OnInit, OnDestroy {
    private lastTravelRatesKey: string | null = null;
    private active = true;

    process: Process;
    superUser = false;
    userInfo: IUserInfo;
    directReport: IDirectReport;
    canEdit = true;
    travelTypeId: TravelTypeEnum = TravelTypeEnum.PerDiem;
    summary: IExpenseSummary;
    detail: IExpenseLine;
    reportDateEnabled = true;
    purposeRequired = false;
    receiptDescriptionRequired = false;
    saving = false;
    successMessage: string;
    failureMessage: string;

    tasks$: Observable<ITask[]>;
    expenseTasks$: Observable<IExpenseTask[]>;
    expenseItems$: Observable<IExpenseItem[]>;
    locales$: Observable<ILocale[]>;
    perDiemTypes$: Observable<IPerDiemLocale[]>;
    tripLegTypes$: Observable<ITripLegType[]>;
    travelModes$: Observable<ITravelMode[]>;
    travelRates$: Observable<ITravelRate[]>;
    selectedMileageRate$: Observable<ITravelRate>;
    userPaymentMethods$: Observable<UserPaymentMethod[]>;
    minDate$: Observable<Date>;
    maxDate$: Observable<Date>;

    constructor(
        public dialogRef: DynamicDialogRef,
        public config: DynamicDialogConfig,
        private log: LoggingService,
        private store: Store,
        private expenseService: ExpenseService,
        private cd: ChangeDetectorRef
    ) {
        const data = (config.data || {}) as ExpenseEntryDialogData;
        this.process = data.process;
        this.superUser = !!data.superUser;
        this.userInfo = data.userInfo;
        this.canEdit = data.canEdit !== false;
        this.travelTypeId = data.travelTypeId || TravelTypeEnum.PerDiem;
        const defaultErTypeId =
            this.process?.ProcessID === 147 || this.process?.ProcessID === 152
                ? ExpenseRequestTypeEnum.Travel
                : ExpenseRequestTypeEnum.Expense;
        this.summary = data.summary || {
            ExpReportNbr: -1,
            ReportDate: new Date(),
            ERTypeID: defaultErTypeId
        };
        if (this.summary.ERTypeID == null) {
            this.summary = { ...this.summary, ERTypeID: defaultErTypeId };
        }
        this.detail = data.detail || {
            ExpReportNbr: this.summary.ExpReportNbr,
            TravelTypeID: this.travelTypeId
        };
        this.directReport = {
            ...data.directReport,
            MaxWorkDate: moment(data.directReport?.MaxWorkDate).toISOString(),
            MinWorkDate: moment(data.directReport?.MinWorkDate).toISOString()
        };
        // Match list popup gating: editable when not Validated/Approved and
        // report date is in the current entry window (canEdit). Datepicker
        // minDate/maxDate still constrain allowed values.
        this.reportDateEnabled = this.canEdit;
        this.purposeRequired = !!this.userInfo?.ExpensePurposeRequired;

        this.log.verbose({
            type: 'ExpenseEntryFeatureComponent:constructor',
            travelTypeId: this.travelTypeId,
            summary: this.summary,
            detail: this.detail,
            canEdit: this.canEdit
        });
    }

    ngOnInit(): void {
        this.setupObservables();
        this.loadLookups();
    }

    ngOnDestroy(): void {
        this.active = false;
    }

    onSave(payload: ExpenseEntrySavePayload): void {
        if (this.saving || !this.canEdit) {
            return;
        }
        this.saving = true;
        this.failureMessage = null;
        this.successMessage = null;
        this.cd.markForCheck();

        const line = { ...payload.line };
        // Match legacy expense-add: mark lines entered as super user so
        // ef_func_requiresoversign returns true for the employee oversign column.
        if (this.superUser) {
            line.SuperUser = true;
            line.SuperUserListID = this.userInfo?.EmployeeListID;
        }

        this.expenseService
            .putExpenseReportDetail(line)
            .pipe(take(1))
            .subscribe({
                next: result => {
                    this.log.verbose({
                        type: 'ExpenseEntryFeatureComponent:onSave:success',
                        result,
                        superUser: this.superUser
                    });
                    this.saving = false;
                    // PUT body is a StatusDesc / info message from the sproc — show it.
                    const message = this.normalizeSaveResultMessage(result);
                    this.failureMessage = null;
                    this.successMessage =
                        message || 'Expense report saved.';
                    this.cd.markForCheck();
                    this.dialogRef.close({
                        saved: true,
                        result,
                        message: this.successMessage
                    });
                },
                error: err => {
                    this.log.error({
                        type: 'ExpenseEntryFeatureComponent:onSave:error',
                        err
                    });
                    this.saving = false;
                    this.failureMessage =
                        err?.error?.Message ||
                        (typeof err?.error === 'string' ? err.error : null) ||
                        err?.message ||
                        'Unable to save expense';
                    this.cd.markForCheck();
                }
            });
    }

    onCancel(): void {
        this.dialogRef.close({ saved: false });
    }

    /** Strip JSON quotes / whitespace from ExpenseReportDetail PUT body. */
    private normalizeSaveResultMessage(result: unknown): string {
        if (result == null) {
            return '';
        }
        let text = String(result).trim();
        if (
            (text.startsWith('"') && text.endsWith('"')) ||
            (text.startsWith("'") && text.endsWith("'"))
        ) {
            text = text.slice(1, -1).trim();
        }
        return text;
    }

    onTravelRatesNeeded(event: ExpenseEntryTravelRatesRequest): void {
        const key = JSON.stringify(event);
        if (key === this.lastTravelRatesKey) {
            return;
        }
        this.lastTravelRatesKey = key;
        this.store.dispatch(new fromExpense.SetTravelRates(null));
        this.store.dispatch(new fromExpense.SelectTravelRate(null));
        this.store.dispatch(new fromExpense.LoadTravelRates(event.request));
    }

    private setupObservables(): void {
        this.tasks$ = this.store.pipe(select(fromLookup.getTasksLookup));
        this.expenseItems$ = this.store.pipe(
            select(fromLookup.getExpenseItems)
        );
        this.locales$ = this.store.pipe(select(fromLookup.getLocalesLookup));
        this.perDiemTypes$ = this.store.pipe(
            select(fromLookup.getPerDiemTypesLookup)
        );
        this.tripLegTypes$ = this.store.pipe(
            select(fromExpense.getTripLegTypes)
        );
        this.travelModes$ = this.store.pipe(
            select(fromExpense.getExpenseTravelModes)
        );
        this.travelRates$ = this.store.pipe(select(fromExpense.getTravelRates));
        this.selectedMileageRate$ = this.store.pipe(
            select(fromExpense.getSelectedRate)
        );
        this.expenseTasks$ = this.store.pipe(select(fromExpense.getExpenseTasks));
        this.userPaymentMethods$ = this.store.pipe(
            select(fromExpense.getUserPaymentMethods),
            map((methods: UserPaymentMethod[]) => methods || [])
        );

        const lag$ = this.store.pipe(
            select(fromSettings.selectEntity('ExpenseReportLagDays')),
            startWith(null)
        );
        this.minDate$ = lag$.pipe(
            map(setting => {
                const lagDays =
                    setting?.SettingValue != null && setting.SettingValue !== ''
                        ? parseInt(String(setting.SettingValue), 10)
                        : 0;
                const lag = isNaN(lagDays) ? 0 : lagDays;
                return moment(this.directReport.MinWorkDate)
                    .subtract(lag, 'days')
                    .toDate();
            })
        );
        this.maxDate$ = of(moment(this.directReport.MaxWorkDate).toDate());

        this.store
            .pipe(
                select(fromSettings.getSettingBooleanValue('ReceiptDescRequired')),
                takeWhile(() => this.active)
            )
            .subscribe(required => {
                this.receiptDescriptionRequired = !!required;
                this.cd.markForCheck();
            });
    }

    private loadLookups(): void {
        const employeeListId =
            this.directReport?.EmployeeListID || this.userInfo?.EmployeeListID;

        this.store.dispatch(new fromLookup.LoadPerDiemTypes());
        this.store.dispatch(new fromLookup.LoadLocales());
        this.store.dispatch(new fromLookup.LoadTravelTypes());
        this.store.dispatch(new fromLookup.LoadExpenseItems());
        this.store.dispatch(new fromLookup.LoadCountries());
        this.store.dispatch(new fromLookup.LoadAreas());
        this.store.dispatch(new fromExpense.LoadTravelMode());
        this.store.dispatch(new fromExpense.LoadTripLegTypes());
        this.store.dispatch(
            fromSettings.getCompanySetting({ SettingType: 'PurposeRequired' })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ReceiptDescRequired'
            })
        );
        this.store.dispatch(
            fromSettings.getCompanySetting({
                SettingType: 'ExpenseReportLagDays'
            })
        );

        if (employeeListId) {
            this.store.dispatch(new fromExpense.LoadExpenseTasks(employeeListId));
            this.store.dispatch(
                new fromExpense.LoadUserPaymentMethods(employeeListId)
            );
        }
    }
}
